import { inject, injectable } from 'inversify'

import { Message, MessageType } from '@infrastructure/managers/messages/messages'

import { MessageManager } from '../messages/message-manager'
import { NotificationsManager } from '../notifications/notifications-manager'
import { TrackingManager } from '../tracking/tracking-manager'

interface Error {
  code?: string
  status?: number
  message?: any
  type?: 'error' | 'warning'
}

@injectable()
export class AIPromptsErrorManager {
  constructor(
    @inject(TrackingManager) private readonly trackingManager: TrackingManager,
    @inject(NotificationsManager) private readonly notificationsManager: NotificationsManager,
    @inject(MessageManager) private readonly messageManager: MessageManager,
  ) {}

  public handleError(error: Error | undefined): void {
    if (error?.status === 429) {
      this.handle429Error()
      return
    }

    this.handleDefaultError(error)
  }

  private handle429Error() {
    const { code, message } = this.messageManager.getQuotaMessage()
    this.trackingManager.track('message', code)
    this.notificationsManager.notify(message)
  }

  private handleDefaultError(error: Error | undefined) {
    this.trackingManager.track('message', error?.message?.code ?? 'ai-prompts', {
      errorDetails: {
        originalMessage: error?.message,
        code: error?.code,
      },
    })
    const message = this.getDefaultErrorMessage(error)
    this.notificationsManager.notify(message)
  }

  private getDefaultErrorMessage(error: Error | undefined): Message {
    const message =
      this.messageManager.getMessage(error?.code as MessageType) ??
      this.messageManager.getMessage(error?.message?.code as MessageType) ??
      this.messageManager.getMessage(error?.message?.message as MessageType) ??
      this.messageManager.getMessage(error?.message as MessageType)

    const defaultError = this.messageManager.getDefaultMessage()

    const finalMessage: Message = message ?? {
      level: error?.message?.message ? 'warning' : 'error',
      title: error?.message?.message ? 'Warning' : defaultError.title,
      message: error?.message?.message ?? defaultError.message,
    }

    return finalMessage
  }
}
